import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import JSZip from 'jszip';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  constructor(
    private httpService: HttpService,
    private http: HttpClient,
  ) {}

  async addDocument(body: object): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.Documents}`, body),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async getDocumentsByUserId(queryParams: any): Promise<any> {
    try {
      const documents = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.Documents}`, {
          params: queryParams,
        }),
      );
      if (queryParams && queryParams.page > 0) {
        return this.httpService.decodeTokenPagination(documents);// Pagination decoding for paginated responses
      } else {
        return this.httpService.decodeToken(documents);// Regular decoding for non-paginated responses
      }
    } catch (error) {
      throw error;
    }
  }

  async uploadMedia(formData: FormData): Promise<any> {
    try {
      const response: any = await firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.documents_media}`, formData),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async documentById(id: number): Promise<any> {
    try {
      return firstValueFrom(this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.Documents}/${id}`));
    } catch (error) {
      throw error;
    }
  }

  async deleteDocumentById(id: number): Promise<any> {
    try {
      const resp = await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.Documents}/${id}`),
      );
      return resp;
    } catch (error) {
      throw error;
    }
  }

  async getVideoTutorial(queryParams: object): Promise<any> {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.tutorial_videos}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }

  async getTutorialById(id: number): Promise<any> {
    try {
      return firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.tutorial_videos}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async downloadAllMedia(documents: any[]): Promise<void> {
    try {
      // Handle empty documents array
      if (!documents || documents.length === 0) {
        throw new Error('No documents to download');
      }

      const localZipFile = new JSZip();

      // Download all documents concurrently for better performance
      const downloadPromises = documents.map(async (document) => {
        const docUrl =
          document.typeName === 'doc' ? document.documents_media.document.url : document.documents_media.image.url;

        const blob: any = await firstValueFrom(
          this.http.get(docUrl, {
            responseType: 'blob',
            headers: { Accept: 'application/json' },
          }),
        );

        const fileName =
          document.typeName === 'doc' ? document.documents_media.document.name : document.documents_media.image.name;

        localZipFile.file(fileName, blob, { binary: true });
      });

      // Wait for all downloads to complete
      await Promise.all(downloadPromises);

      // Generate and save zip file - NOW AWAITED properly
      const content = await localZipFile.generateAsync({ type: 'blob' });
      FileSaver.saveAs(content, 'Respond-Edu-Documents.zip');
    } catch (error) {
      throw error;
    }
  }

  async downloadSingleDoc(docUrl: string, document: any): Promise<void> {
    try {
      this.http
        .get(docUrl, {
          responseType: 'blob',
          headers: { Accept: 'application/json' },
        })
        .subscribe((blob: any) => {
          FileSaver.saveAs(
            blob,
            document.typeName === 'doc'
              ? `Respond-Edu-Document-${document.documents_media.document.name}`
              : `Respond-Edu-Document-${document.documents_media.image.name}`,
          );
        });
    } catch (error) {
      throw error;
    }
  }

  async getDocumentsBySchoolId(queryParams: object): Promise<any> {
    try {
      const documents = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_all_documents}`, {
          params: queryParams,
        }),
      );
      return documents;
    } catch (error) {
      throw error;
    }
  }
}
